/**
 * /forgot-password — request a password-reset email.
 *
 * We always show the same success message regardless of whether the email
 * is on file. The backend behaves the same way; this is account-enumeration
 * defence (someone probing valid emails can't tell which addresses exist
 * by hitting this endpoint).
 */

import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link } from "@tanstack/react-router";

import { AuthShell } from "../components/AuthShell";
import { Button, Field, Icon, Input, Stack } from "../components/ui";
import { t } from "../i18n";
import { forgotPasswordSchema } from "../lib/schemas";
import { trpc } from "../lib/trpc";
import { fieldError } from "../lib/validation";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const request = trpc.auth.requestPasswordReset.useMutation();

  const form = useForm({
    defaultValues: { email: "" },
    validators: {
      onChange: forgotPasswordSchema,
      onSubmit: forgotPasswordSchema,
    },
    onSubmit: ({ value }) => {
      request.mutate({ email: value.email.trim() });
    },
  });

  if (request.data?.ok) {
    return (
      <AuthShell
        title={t.forgotPassword.successTitle}
        description={t.forgotPassword.successDescription}
      >
        <Stack gap={3}>
          <div className="flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <Icon name="mail" size="lg" />
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-fg text-sm hover:opacity-90"
          >
            {t.login.backToLogin}
          </Link>
        </Stack>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t.forgotPassword.title} description={t.forgotPassword.description}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="email">
          {(field) => (
            <Field label={t.forgotPassword.emailLabel} error={fieldError(field)}>
              <Input
                type="email"
                autoComplete="email"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={request.isPending}
                iconLeft="mail"
                invalid={!!fieldError(field)}
                autoFocus
              />
            </Field>
          )}
        </form.Field>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={request.isPending}
          disabled={request.isPending}
        >
          {request.isPending ? t.forgotPassword.submitting : t.forgotPassword.submitButton}
        </Button>
        {request.isError && (
          <p className="text-rose-600 text-xs dark:text-rose-400" role="alert">
            {request.error.message}
          </p>
        )}
        <div className="pt-1 text-center text-ink-500 text-xs">
          <Link
            to="/"
            className="text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline dark:text-accent-300 dark:hover:text-accent-200"
          >
            {t.login.backToLogin}
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
